"use client"

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import axios from "axios"
import { ChevronDownIcon, LogInIcon, LogOutIcon, UserIcon } from "lucide-react"
import Link from "next/link"
import { useRouter } from "next/navigation"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

interface AuthMeResponse {
  user: {
    name?: string
  } | null
}

interface CartItemsResponse {
  ok: boolean
  cartCount: number
  items: unknown[]
  totals: {
    subtotal: number
    total: number
    currency: string
  }
}

const WHITESPACE_REGEX = /\s+/

const EMPTY_CART: CartItemsResponse = {
  ok: true,
  cartCount: 0,
  items: [],
  totals: {
    subtotal: 0,
    total: 0,
    currency: "BRL",
  },
}

export const getUserMenuLabel = (displayName: string | undefined): string => {
  if (!displayName) {
    return "Entrar"
  }

  const firstName = displayName.trim().split(WHITESPACE_REGEX)[0]
  return firstName || "Entrar"
}

export default function UserMenu() {
  const router = useRouter()
  const queryClient = useQueryClient()

  const { data } = useQuery({
    queryKey: ["auth", "me"],
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    retry: false,
    queryFn: async (): Promise<AuthMeResponse> => {
      try {
        const { data } = await axios.get<AuthMeResponse>("/api/auth/me", {
          params: { t: Date.now() },
        })

        return data
      } catch {
        return { user: null }
      }
    },
  })

  const logout = useMutation({
    mutationFn: () => axios.post("/api/auth/logout"),
    onMutate: () => {
      const previousAuthMe = queryClient.getQueryData<AuthMeResponse>([
        "auth",
        "me",
      ])
      const previousCartItems = queryClient.getQueryData<CartItemsResponse>([
        "cart",
        "items",
      ])

      queryClient.setQueryData<AuthMeResponse>(["auth", "me"], {
        user: null,
      })
      queryClient.setQueryData<CartItemsResponse>(["cart", "items"], EMPTY_CART)

      return { previousAuthMe, previousCartItems }
    },
    onError: (_error, _variables, context) => {
      if (context?.previousAuthMe) {
        queryClient.setQueryData<AuthMeResponse>(
          ["auth", "me"],
          context.previousAuthMe
        )
      }

      if (context?.previousCartItems) {
        queryClient.setQueryData<CartItemsResponse>(
          ["cart", "items"],
          context.previousCartItems
        )
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["auth", "me"],
        refetchType: "all",
      })
      await queryClient.invalidateQueries({
        queryKey: ["cart", "items"],
        refetchType: "all",
      })

      router.replace("/")
    },
  })

  const isAuthenticated = Boolean(data?.user)
  const displayName = data?.user?.name

  if (!isAuthenticated) {
    return (
      <Button asChild size='sm' variant='link'>
        <Link href='/entrar'>
          <LogInIcon aria-hidden='true' size={16} />
          Entrar
        </Link>
      </Button>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size='sm' variant='link'>
          <UserIcon aria-hidden='true' size={16} />
          {getUserMenuLabel(displayName)}
          <ChevronDownIcon aria-hidden='true' size={14} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end'>
        <DropdownMenuItem asChild>
          <Link href='/minha-conta'>
            <UserIcon aria-hidden='true' size={14} />
            Minha conta
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          disabled={logout.isPending}
          onSelect={() => logout.mutate()}
          variant='destructive'
        >
          <LogOutIcon aria-hidden='true' size={14} />
          Sair
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
